import { supabase } from './supabase';

export interface BankTransactieRow {
  id: string;
  /** Herkomst: 'bank' (ABN via Enable Banking) of 'mypos' (pinterminal). */
  source: 'bank' | 'mypos';
  booking_date: string;
  amount: number;
  currency: string;
  counterparty_name: string | null;
  counterparty_iban: string | null;
  remittance: string | null;
  status: string;
  match_type: string | null;
  match_confidence: string | null;
  matched_parent_type: 'Invoice__c' | 'Werk_Order__c' | null;
  matched_parent_id: string | null;
  matched_parent_name: string | null;
  sf_transactie_id: string | null;
  ignore_reason: string | null;
  created_at: string;
  processed_at: string | null;
  processed_by: string | null;
}

export interface BankImportSummary {
  fetched: number;
  new: number;
  skipped: number;
  autoPosted: number;
  autoIgnored: number;
  review: number;
  consentProblem: boolean;
  consentExpiresAt: string | null;
  errors: string[];
  /** Pin-ontvangsten (MyPOS) — null als de sync faalde, enabled=false bij flag uit. */
  mypos?: { enabled: boolean; new: number; review: number; resolved: number } | null;
}

/** Dagtotalen voor de bank-kolom op /controle — zelfde shape als de CAMT-parser. */
export interface ControleDagtotalen {
  iban: string;
  creditsByDay: Map<string, number>;
  filteredTotal: number;
  filteredCount: number;
  entries: { datum: string; bedrag: number; description: string; senderName: string }[];
  summary: BankImportSummary;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

export async function listBankReview(): Promise<BankTransactieRow[]> {
  const headers = await authHeaders();
  const response = await fetch('/api/bank/transacties?action=list', { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Kon bankontvangsten niet laden');
  }
  const payload = (await response.json()) as { items?: BankTransactieRow[] };
  return Array.isArray(payload.items) ? payload.items : [];
}

export type LinkBankTransactieResult =
  | { ok: true; transactieIds: string[] }
  | { ok: false; error: string; code?: string };

/** Eén toewijzing binnen een ontvangst: welk record, en voor hoeveel. */
export interface BankAllocation {
  parentType: 'Invoice__c' | 'Werk_Order__c';
  parentId: string;
  amount: number;
}

/**
 * Koppelt een bankontvangst aan één of meer records. Bij een verzamelbetaling
 * moet de som van de deelbedragen exact gelijk zijn aan de ontvangst — de API
 * weigert een verdeling die niet sluit.
 */
export async function linkBankTransactie(
  id: string,
  allocations: BankAllocation[]
): Promise<LinkBankTransactieResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/bank/transacties?action=link', {
    method: 'POST',
    headers,
    body: JSON.stringify({ id, allocations }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, error: payload.error || 'Koppelen mislukt', code: payload.code };
  }
  return {
    ok: true,
    transactieIds: Array.isArray(payload.transactieIds)
      ? payload.transactieIds
      : [payload.transactieId].filter(Boolean),
  };
}

export async function ignoreBankTransactie(
  id: string,
  reden: string
): Promise<{ ok: boolean; error?: string }> {
  const headers = await authHeaders();
  const response = await fetch('/api/bank/transacties?action=ignore', {
    method: 'POST',
    headers,
    body: JSON.stringify({ id, reden }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Negeren mislukt' }));
    return { ok: false, error: err.error || 'Negeren mislukt' };
  }
  return { ok: true };
}

export async function fetchControleDagtotalen(month: string): Promise<ControleDagtotalen> {
  const headers = await authHeaders();
  const response = await fetch('/api/bank/transacties?action=controle-dagtotalen', {
    method: 'POST',
    headers,
    body: JSON.stringify({ month }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Bank ophalen mislukt' }));
    throw new Error(err.error || 'Bank ophalen mislukt');
  }
  const payload = await response.json();
  return {
    iban: payload.iban || '',
    creditsByDay: new Map(Object.entries(payload.creditsByDay || {})),
    filteredTotal: payload.filteredTotal || 0,
    filteredCount: payload.filteredCount || 0,
    entries: Array.isArray(payload.entries) ? payload.entries : [],
    summary: payload.summary,
  };
}

/** Kandidaat voor een pinontvangst: order die die dag is afgeleverd (rit of balie-afhaal). */
export interface PinKandidaat {
  parentType: 'Invoice__c' | 'Werk_Order__c';
  parentId: string;
  label: string;
  openAmount: number;
  totalAmount: number;
  bron: 'Terugbrengen' | 'Balie';
  exact: boolean;
  /** Totaal ≈ pinbedrag maar openstaand 0 — waarschijnlijk al betaald (dubbele betaling?). */
  paidTotalMatch: boolean;
}

export async function fetchPinKandidaten(
  datum: string,
  bedrag: number,
  signal?: AbortSignal
): Promise<PinKandidaat[]> {
  const headers = await authHeaders();
  const response = await fetch(
    `/api/bank/transacties?action=pin-kandidaten&datum=${encodeURIComponent(datum)}&bedrag=${encodeURIComponent(bedrag)}`,
    { headers, signal }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Kandidaten laden mislukt' }));
    throw new Error(err.error || 'Kandidaten laden mislukt');
  }
  const payload = (await response.json()) as { kandidaten?: PinKandidaat[] };
  return Array.isArray(payload.kandidaten) ? payload.kandidaten : [];
}

/**
 * Waarschijnlijke post bij een bankontvangst zonder harde suggestie. `reden`
 * zegt waarom hij in de lijst staat — referentie is het sterkste bewijs.
 */
export interface BankKandidaat {
  parentType: 'Invoice__c' | 'Werk_Order__c';
  parentId: string;
  label: string;
  openAmount: number;
  totalAmount: number;
  reden: 'referentie' | 'bedrag' | 'naam';
  exact: boolean;
}

export async function fetchBankKandidaten(
  transactieId: string,
  signal?: AbortSignal
): Promise<BankKandidaat[]> {
  const headers = await authHeaders();
  const response = await fetch(
    `/api/bank/transacties?action=bank-kandidaten&id=${encodeURIComponent(transactieId)}`,
    { headers, signal }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Kandidaten laden mislukt' }));
    throw new Error(err.error || 'Kandidaten laden mislukt');
  }
  const payload = (await response.json()) as { kandidaten?: BankKandidaat[] };
  return Array.isArray(payload.kandidaten) ? payload.kandidaten : [];
}

export async function syncBank(dateFrom?: string): Promise<BankImportSummary> {
  const headers = await authHeaders();
  const response = await fetch('/api/bank/transacties?action=sync', {
    method: 'POST',
    headers,
    body: JSON.stringify(dateFrom ? { dateFrom } : {}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Sync mislukt' }));
    throw new Error(err.error || 'Sync mislukt');
  }
  return response.json();
}
